import React from "react";
import {
  Check,
  Copy,
  ExternalLink,
  PackageOpen,
  Search,
  ShoppingCart,
} from "lucide-react";
import { materialOrderItems, orderCategories } from "./data/orderCatalog";

const STORAGE_KEY = "hydro-terrace-order-cart-v1";

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function money(value) {
  if (!Number.isFinite(value)) return "Check price";
  return `$${value.toFixed(2)}`;
}

function statusClass(status) {
  if (status === "Buy now") return "buy-now";
  if (status === "Future/test") return "future-test";
  if (status === "Hold") return "hold";
  return "optional";
}

function seedItemsFromRows(rows) {
  return rows
    .filter((row) => row.purchase_status && row.purchase_status !== "Not in current plan" && row.purchase_url)
    .map((row) => ({
      id: `seed-${row.id}`,
      category: "Seeds",
      status: row.purchase_status,
      name: row.purchase_item || row.microgreen,
      supplier: row.purchase_url.includes("trueleafmarket") ? "True Leaf Market" : "Seed supplier",
      package: row.purchase_package || "Select package",
      estimatedPrice: Number(row.purchase_price_usd) || null,
      url: row.purchase_url,
      note: row.purchase_role || row.purchase_risk || row.purchase_source_status,
      sourceStatus: row.purchase_source_status,
      defaultSelected: row.purchase_status === "Buy now",
    }));
}

export default function OrderPage({ rows, onMessage }) {
  const saved = React.useMemo(loadCart, []);
  const seedItems = React.useMemo(() => seedItemsFromRows(rows), [rows]);
  const items = React.useMemo(() => [...seedItems, ...materialOrderItems], [seedItems]);
  const [selectedIds, setSelectedIds] = React.useState(saved.selectedIds || []);
  const [quantities, setQuantities] = React.useState(saved.quantities || {});
  const [category, setCategory] = React.useState("All");
  const [search, setSearch] = React.useState("");
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (initializedRef.current || items.length === 0) return;
    initializedRef.current = true;
    if (Array.isArray(saved.selectedIds)) return;
    setSelectedIds(items.filter((item) => item.defaultSelected).map((item) => item.id));
  }, [items, saved.selectedIds]);

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedIds, quantities }));
  }, [quantities, selectedIds]);

  const selected = React.useMemo(() => items.filter((item) => selectedIds.includes(item.id)), [items, selectedIds]);
  const knownSubtotal = selected.reduce((sum, item) => sum + (item.estimatedPrice || 0) * (quantities[item.id] || 1), 0);
  const unknownPriceCount = selected.filter((item) => !Number.isFinite(item.estimatedPrice)).length;
  const currentSeedCount = seedItems.filter((item) => item.status === "Buy now").length;
  const filtered = items.filter((item) => {
    const haystack = `${item.name} ${item.supplier} ${item.note} ${item.status}`.toLowerCase();
    return (category === "All" || item.category === category) && (!search || haystack.includes(search.toLowerCase()));
  });

  function toggleItem(id) {
    setSelectedIds((current) => current.includes(id) ? current.filter((itemId) => itemId !== id) : [...current, id]);
  }

  function selectBaseline() {
    setSelectedIds(items.filter((item) => item.defaultSelected).map((item) => item.id));
  }

  function includeFuture() {
    setSelectedIds(items.filter((item) => item.status !== "Hold").map((item) => item.id));
  }

  async function copyOrderList() {
    const lines = [
      "HydroTerrace order list",
      `Selected items: ${selected.length}`,
      `Known-price subtotal: ${money(knownSubtotal)}`,
      "",
      ...selected.map((item) => {
        const quantity = quantities[item.id] || 1;
        return `${quantity} x ${item.name} | ${item.package} | ${item.supplier} | ${item.url}`;
      }),
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      onMessage?.("Order list copied.");
    } catch {
      onMessage?.("Could not copy the order list in this browser.");
    }
  }

  function openSelected() {
    for (const item of selected) window.open(item.url, "_blank", "noopener,noreferrer");
    onMessage?.(`Opened ${selected.length} supplier link(s). Your browser may group or block multiple tabs.`);
  }

  return (
    <section className="orderPage">
      <header className="orderHeader">
        <div>
          <span className="eyebrow">Sourcing workspace</span>
          <h2>Quick order</h2>
          <p>Current seeds and baseline paper materials are selected. Future tests and held additives remain visible without entering the default cart.</p>
        </div>
        <div className="orderActions">
          <button type="button" onClick={copyOrderList}><Copy size={16} /> Copy order list</button>
          <button type="button" className="openOrderButton" onClick={openSelected} disabled={selected.length === 0}><ExternalLink size={16} /> Open selected links</button>
        </div>
      </header>

      <div className="orderSummary">
        <div><ShoppingCart size={19} /><strong>{selected.length}</strong><span>Selected items</span></div>
        <div><Check size={19} /><strong>{currentSeedCount}</strong><span>Current seeds</span></div>
        <div><PackageOpen size={19} /><strong>{money(knownSubtotal)}</strong><span>Known subtotal</span></div>
        <div><ExternalLink size={19} /><strong>{unknownPriceCount}</strong><span>Prices to confirm</span></div>
      </div>

      <div className="orderToolbar">
        <div className="orderSearch">
          <Search size={16} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search seeds, pulp, tools, packaging" />
        </div>
        <div className="orderCategoryTabs" role="tablist" aria-label="Order categories">
          {["All", ...orderCategories].map((item) => (
            <button key={item} type="button" className={category === item ? "active" : ""} onClick={() => setCategory(item)}>{item}</button>
          ))}
        </div>
        <div className="orderPresets">
          <button type="button" onClick={selectBaseline}>Baseline kit</button>
          <button type="button" onClick={includeFuture}>Add future tests</button>
          <button type="button" onClick={() => setSelectedIds([])}>Clear</button>
        </div>
      </div>

      <div className="orderSections">
        {orderCategories.map((section) => {
          const sectionItems = filtered.filter((item) => item.category === section);
          if (sectionItems.length === 0) return null;
          return (
            <section className="orderSection" key={section}>
              <header>
                <h3>{section}</h3>
                <span>{sectionItems.filter((item) => selectedIds.includes(item.id)).length} selected / {sectionItems.length}</span>
              </header>
              <div className="orderItemList">
                {sectionItems.map((item) => {
                  const isSelected = selectedIds.includes(item.id);
                  const quantity = quantities[item.id] || 1;
                  return (
                    <article className={`orderItem ${isSelected ? "selected" : ""}`} key={item.id}>
                      <button className="orderCheck" type="button" aria-label={`${isSelected ? "Remove" : "Add"} ${item.name}`} onClick={() => toggleItem(item.id)}>
                        {isSelected ? <Check size={16} /> : null}
                      </button>
                      <div className="orderItemMain">
                        <div>
                          <span className={`orderStatus ${statusClass(item.status)}`}>{item.status}</span>
                          <h4>{item.name}</h4>
                          <p>{item.note}</p>
                        </div>
                        <dl>
                          <div><dt>Supplier</dt><dd>{item.supplier}</dd></div>
                          <div><dt>Package</dt><dd>{item.package}</dd></div>
                          <div><dt>Estimate</dt><dd>{money(item.estimatedPrice)}</dd></div>
                        </dl>
                        {item.sourceStatus && <small>{item.sourceStatus}</small>}
                      </div>
                      <label className="orderQuantity">
                        <span>Qty</span>
                        <input type="number" min="1" max="99" value={quantity} onChange={(event) => setQuantities({ ...quantities, [item.id]: Math.max(1, Math.min(99, Number(event.target.value) || 1)) })} />
                      </label>
                      <a className="orderLink" href={item.url} target="_blank" rel="noreferrer">Order <ExternalLink size={15} /></a>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
